import {
  MAX_ECG_DURATION,
  EVERBEAT_WAVEFORM_DECREASE,
  HOLTER_FILTER_FREQUENCY,
  HOLTER_FILTER_RESONANCE,
} from './constants';
import dsp from './dsp';

var paperSpeedMode = 3;

/**
 * @param {Array<number>} samples
 * @param {number} sampleRate
 * @return {Array<number>}
 */

const getFilteredECGSamples = (samples, sampleRate, decriesPercentage = EVERBEAT_WAVEFORM_DECREASE) => {
  const maxSampleLength = MAX_ECG_DURATION * sampleRate;

  const lengthFilteredSamples = samples.length > maxSampleLength ? samples.slice(-maxSampleLength) : samples;

  return lengthFilteredSamples.map((el) => el * (decriesPercentage / 100));
};

function getPaperSpeedMode() {
  return paperSpeedMode;
}

function setPaperSpeedMode(mode) {
  paperSpeedMode = mode;
}

const cutoffFrequency = 0.5; // cutoff frequency in Hz
const samplingFrequency = 512; // sampling frequency in Hz
const a = 0.99; // 1 - Math.exp(-2 * Math.PI * cutoffFrequency / samplingFrequency);
let x_prev = 0; // previous input sample
let y_prev = 0; // previous output sample

function highPassFilterECG(x) {
  // compute current output sample
  const y = a * y_prev + a * (x - x_prev);
  // update previous input and output samples
  x_prev = x;
  y_prev = y;
  // return current output
  return y;
}

function removeDCBias(ecgSignal) {
  const filteredECG = [];
  for (let i = 0; i < ecgSignal.length; i++) {
    filteredECG[i] = parseInt(highPassFilterECG(ecgSignal[i]));
  }
  return filteredECG;
}
//   function findPeaks(signal) {
//     const peaksArray = scipy.signal.find_peaks(signal)
//     return peaksArray
//   }
function findMaxPeak(signal) {
  // Set the initial maximum peak to the first sample
  let maxPeak = signal[0];

  // Loop through the signal and find the maximum peak
  for (let i = 1; i < signal.length; i++) {
    if (signal[i] > maxPeak) {
      maxPeak = signal[i];
    }
  }

  return maxPeak;
}

function lerp(a, b, ratio) {
  return a + (b - a) * ratio;
}

function clamp(v, min, max) {
  return Math.max(Math.min(v, max), min);
}

function leftPadSamples(samples, count) {
  const missingSampleCount = Math.max(count - samples.length, 0);
  const paddingZeros = new Array(missingSampleCount).fill(0);

  return paddingZeros.concat(samples);
}

function rightPadSamples(samples, count) {
  const missingSampleCount = Math.max(count - samples.length, 0);
  const paddingZeros = new Array(missingSampleCount).fill(0);

  return samples.concat(paddingZeros);
}

function downSample(samples, count, sampleRate) {
  if (count <= 0) {
    return [];
  } else if (samples.length <= count) {
    return samples;
  }

  const stride = samples.length / count;
  const newSamples = [];

  const inputRate = 80;
  const outputRate = inputRate / stride;

  const lowPass = new dsp.Biquad(dsp.DSP.LOWPASS, sampleRate);
  lowPass.setF0(outputRate / 2);
  const filteredSamples = lowPass.process(samples);

  for (let i = 0; i < filteredSamples.length; i += stride) {
    const idx = Math.floor(i);
    newSamples.push(filteredSamples[idx]);
  }

  return newSamples;
}

function upSample(samples, count, sampleRate) {
  if (count <= 0) {
    return [];
  } else if (samples.length >= count) {
    return samples;
  }

  const stride = count / samples.length;
  const newSamples = [];

  for (let i = 0; i < count; i++) {
    const baseIndex = Math.floor(i / stride);

    const a = samples[baseIndex];
    const b = samples[clamp(baseIndex + 1, 0, samples.length - 1)];
    const ratio = (i % stride) / stride;

    const interp = lerp(a, b, ratio);

    newSamples.push(interp);
  }

  return newSamples;
}

function resample(samples, count, sampleRate) {
  if (samples.length < count) {
    return upSample(samples, count, sampleRate);
  } else if (samples.length > count) {
    return downSample(samples, count, sampleRate);
  } else {
    return samples;
  }
}

const lowPassFilter = (samples, frequency, resonance, sampleRate = 512) => {
  const lowPassFilter = new dsp.IIRFilter(dsp.DSP.LOWPASS, frequency, resonance, sampleRate);
  return samples.map((sample) => Math.round(lowPassFilter.process(sample)));
};

const smoothHolterSamples = (samples, sampleRate = 512) => {
  const lowPassFiltered = lowPassFilter(samples, HOLTER_FILTER_FREQUENCY, HOLTER_FILTER_RESONANCE, sampleRate);

  return removeDCBias(lowPassFiltered);
};

export {
  getPaperSpeedMode,
  setPaperSpeedMode,
  removeDCBias,
  leftPadSamples,
  rightPadSamples,
  resample,
  getFilteredECGSamples,
  smoothHolterSamples,
};
